import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';
import { apiRequest, API_BASE_URL } from '../config/api';

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

// Super admin endpoints authenticate with a separate token (localStorage 'superAdminToken'),
// not the regular admin/player tokens that apiRequest() attaches.
function superAdminApiRequest(path: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('superAdminToken');
  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(options.headers || {}),
    },
  });
}

export class ImageUploadService {
  /**
   * Upload a profile picture to Firebase Storage
   * @param file - The image file to upload
   * @param playerId - The player's ID for unique naming
   * @returns Promise<string> - The download URL of the uploaded image
   */
  static async uploadProfilePicture(file: File, playerId: string): Promise<string> {
    try {
      console.log('Starting upload for player ID:', playerId);
      console.log('Storage config:', storage.app.options);
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        throw new Error('File size must be less than 2MB');
      }

      // Resize the image before upload with timeout
      console.log('Starting image resize...');
      const resizedFile = await Promise.race([
        this.resizeImage(file, 300, 300),
        new Promise<File>((_, reject) => 
          setTimeout(() => reject(new Error('Image resize timeout')), 15000)
        )
      ]);
      console.log('Image resized. Original size:', file.size, 'New size:', resizedFile.size);

      // Create a unique filename
      const timestamp = Date.now();
      const sanitizedPlayerId = playerId.replace(/[^a-zA-Z0-9]/g, '');
      const fileName = `profile-pictures/${sanitizedPlayerId}-${timestamp}.png`;
      
      console.log('Upload path:', fileName);
      
      // Create storage reference
      const storageRef = ref(storage, fileName);
      
      // Upload file with cache headers and timeout
      console.log('Starting upload to Firebase Storage...');
      const metadata = {
        cacheControl: 'public, max-age=31536000, immutable',
        contentType: 'image/png',
      };
      
      const snapshot = await Promise.race([
        uploadBytes(storageRef, resizedFile, metadata),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Upload timeout after 30 seconds')), 30000)
        )
      ]);
      console.log('Upload completed:', snapshot);
      
      // Get download URL with timeout
      const downloadURL = await Promise.race([
        getDownloadURL(snapshot.ref),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Get URL timeout')), 10000)
        )
      ]);
      console.log('Download URL obtained:', downloadURL);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      throw error;
    }
  }

  /**
   * Delete a profile picture from Firebase Storage
   * @param imageUrl - The full URL of the image to delete
   */
  static async deleteProfilePicture(imageUrl: string): Promise<void> {
    try {
      // Extract the path from the URL
      const url = new URL(imageUrl);
      const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
      
      if (!pathMatch) {
        throw new Error('Invalid image URL');
      }
      
      const imagePath = decodeURIComponent(pathMatch[1]);
      const imageRef = ref(storage, imagePath);
      
      await deleteObject(imageRef);
    } catch (error) {
      console.error('Error deleting image:', error);
      // Don't throw error for delete operations to avoid breaking the flow
    }
  }

  /**
   * Resize image client-side before upload
   * @param file - The original image file
   * @param maxWidth - Maximum width in pixels
   * @param maxHeight - Maximum height in pixels
   * @param quality - Image quality (0-1)
   * @returns Promise<File> - The resized image file
   */
  static async resizeImage(
    file: File, 
    maxWidth: number = 300, 
    maxHeight: number = 300, 
    quality: number = 0.8
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.warn('Could not get canvas context, using original file');
        resolve(file);
        return;
      }
      
      const img = new Image();
      
      // Add timeout to prevent infinite waiting
      const timeout = setTimeout(() => {
        console.warn('Image resize timed out, using original file');
        resolve(file);
      }, 10000); // 10 second timeout
      
      img.onerror = (error) => {
        console.error('Error loading image for resize:', error);
        clearTimeout(timeout);
        resolve(file); // Resolve with original file instead of rejecting
      };
      
      img.onload = () => {
        clearTimeout(timeout);
        
        try {
          // Calculate new dimensions
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          // Resize image
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const resizedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(resizedFile);
              } else {
                console.warn('Blob creation failed, using original file');
                resolve(file);
              }
            },
            'image/jpeg',
            quality
          );
        } catch (error) {
          console.error('Error during resize:', error);
          resolve(file); // Use original file on error
        }
      };
      
      try {
        img.src = URL.createObjectURL(file);
      } catch (error) {
        console.error('Error creating object URL:', error);
        clearTimeout(timeout);
        resolve(file);
      }
    });
  }

  /**
   * Upload an organization logo via the backend (Firebase Admin SDK bypasses client Storage rules).
   * @param file - The image file to upload
   * @param orgId - The organization's ID for unique naming
   * @returns Promise<string> - The download URL of the uploaded image
   */
  static async uploadOrganizationLogo(file: File, orgId: string): Promise<string> {
    if (!file.type.startsWith('image/')) throw new Error('File must be an image');
    if (file.size > 2 * 1024 * 1024) throw new Error('File size must be less than 2MB');

    const resizedFile = await Promise.race([
      this.resizeImage(file, 500, 500),
      new Promise<File>((_, reject) =>
        setTimeout(() => reject(new Error('Image resize timeout')), 15000)
      ),
    ]);

    const fileBase64 = await fileToDataUrl(resizedFile);
    const response = await superAdminApiRequest(`/api/super-admin/organizations/${orgId}/upload-logo`, {
      method: 'POST',
      body: JSON.stringify({
        fileBase64,
        contentType: resizedFile.type || 'image/jpeg',
      }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || 'Failed to upload organization logo');
    }

    const data = await response.json();
    return data.url;
  }

  /**
   * Upload an organization's public-homepage hero banner image via the backend
   * (Firebase Admin SDK bypasses client Storage rules).
   * Wide format (1920×640) since it's a full-width background image, not a square logo.
   * @param file - The image file to upload
   * @param orgId - The organization's ID for unique naming
   * @returns Promise<string> - The download URL of the uploaded image
   */
  static async uploadOrganizationHeroImage(file: File, orgId: string): Promise<string> {
    if (!file.type.startsWith('image/')) throw new Error('File must be an image');
    if (file.size > 5 * 1024 * 1024) throw new Error('File size must be less than 5MB');

    const resizedFile = await Promise.race([
      this.resizeImage(file, 1920, 640, 0.85),
      new Promise<File>((_, reject) =>
        setTimeout(() => reject(new Error('Image resize timeout')), 15000)
      ),
    ]);

    const fileBase64 = await fileToDataUrl(resizedFile);
    const response = await superAdminApiRequest(`/api/super-admin/organizations/${orgId}/upload-hero-image`, {
      method: 'POST',
      body: JSON.stringify({
        fileBase64,
        contentType: resizedFile.type || 'image/jpeg',
      }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || 'Failed to upload organization hero image');
    }

    const data = await response.json();
    return data.url;
  }

  /**
   * Upload a tournament sponsor logo to Firebase Storage.
   * Resized to 400×200 at 90% quality — wide format suitable for logos.
   * Max accepted file size: 2 MB.
   */
  static async uploadSponsorLogo(file: File, tournamentId: string): Promise<string> {
    if (!file.type.startsWith('image/')) throw new Error('File must be an image');
    if (file.size > 2 * 1024 * 1024) throw new Error('File size must be less than 2 MB');

    const resizedFile = await Promise.race([
      this.resizeImage(file, 400, 200, 0.9),
      new Promise<File>((_, reject) =>
        setTimeout(() => reject(new Error('Image resize timeout')), 15000)
      ),
    ]);

    const fileBase64 = await fileToDataUrl(resizedFile);
    const response = await apiRequest('/api/tournaments/upload-sponsor-logo', {
      method: 'POST',
      body: JSON.stringify({
        fileBase64,
        contentType: resizedFile.type || 'image/jpeg',
        tournamentId,
      }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || 'Failed to upload sponsor logo');
    }

    const data = await response.json();
    return data.url;
  }

  /**
   * Upload a tournament poster image (used as card background on the public page).
   * Resized to 1200×675 (16:9) at 85% quality — optimised for landscape backgrounds.
   * Max accepted file size: 5 MB.
   */
  static async uploadTournamentPoster(file: File): Promise<string> {
    if (!file.type.startsWith('image/')) throw new Error('File must be an image');
    if (file.size > 5 * 1024 * 1024) throw new Error('File size must be less than 5 MB');

    const resizedFile = await Promise.race([
      this.resizeImage(file, 1200, 675, 0.85),
      new Promise<File>((_, reject) =>
        setTimeout(() => reject(new Error('Image resize timeout')), 20000)
      ),
    ]);

    const fileBase64 = await fileToDataUrl(resizedFile);
    const response = await apiRequest('/api/tournaments/upload-poster', {
      method: 'POST',
      body: JSON.stringify({
        fileBase64,
        contentType: resizedFile.type || 'image/jpeg',
      }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || 'Failed to upload tournament poster');
    }

    const data = await response.json();
    return data.url;
  }

  /**
   * Upload an image attached to an announcement pop-up (player or public homepage).
   * Resized to 800×600 at 85% quality. Max accepted file size: 5 MB.
   */
  static async uploadPopupImage(file: File): Promise<string> {
    if (!file.type.startsWith('image/')) throw new Error('File must be an image');
    if (file.size > 5 * 1024 * 1024) throw new Error('File size must be less than 5 MB');

    const resizedFile = await Promise.race([
      this.resizeImage(file, 800, 600, 0.85),
      new Promise<File>((_, reject) =>
        setTimeout(() => reject(new Error('Image resize timeout')), 20000)
      ),
    ]);

    const fileBase64 = await fileToDataUrl(resizedFile);
    const response = await apiRequest('/api/announcements/upload-popup-image', {
      method: 'POST',
      body: JSON.stringify({
        fileBase64,
        contentType: resizedFile.type || 'image/jpeg',
      }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || 'Failed to upload popup image');
    }

    const data = await response.json();
    return data.url;
  }

  /**
   * Upload a photo for a t-shirt design in the admin-managed gear design catalog.
   * Resized to 800×600 at 85% quality. Max accepted file size: 5 MB.
   */
  static async uploadGearDesignImage(file: File): Promise<string> {
    if (!file.type.startsWith('image/')) throw new Error('File must be an image');
    if (file.size > 5 * 1024 * 1024) throw new Error('File size must be less than 5 MB');

    const resizedFile = await Promise.race([
      this.resizeImage(file, 800, 600, 0.85),
      new Promise<File>((_, reject) =>
        setTimeout(() => reject(new Error('Image resize timeout')), 20000)
      ),
    ]);

    const fileBase64 = await fileToDataUrl(resizedFile);
    const response = await apiRequest('/api/season-registration/upload-gear-design-image', {
      method: 'POST',
      body: JSON.stringify({
        fileBase64,
        contentType: resizedFile.type || 'image/jpeg',
      }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || 'Failed to upload gear design image');
    }

    const data = await response.json();
    return data.url;
  }

  /**
   * Upload a size chart image (t-shirt/polo/shorts) shown to players when picking a size.
   * Kept at a higher resolution than the design photos since size charts carry small
   * table text/labels that need to stay legible. Max accepted file size: 5 MB.
   */
  static async uploadGearSizeChartImage(file: File): Promise<string> {
    if (!file.type.startsWith('image/')) throw new Error('File must be an image');
    if (file.size > 5 * 1024 * 1024) throw new Error('File size must be less than 5 MB');

    const resizedFile = await Promise.race([
      this.resizeImage(file, 1200, 1600, 0.9),
      new Promise<File>((_, reject) =>
        setTimeout(() => reject(new Error('Image resize timeout')), 20000)
      ),
    ]);

    const fileBase64 = await fileToDataUrl(resizedFile);
    const response = await apiRequest('/api/season-registration/upload-gear-design-image', {
      method: 'POST',
      body: JSON.stringify({
        fileBase64,
        contentType: resizedFile.type || 'image/jpeg',
      }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || 'Failed to upload size chart image');
    }

    const data = await response.json();
    return data.url;
  }
}

export default ImageUploadService;